/**
 * PlanContext — Provides current user plan + feature checks across the app
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import billingService from '@services/billingService';
import { useAuth } from '@hooks/useAuth';

const PlanContext = createContext(null);

export const PLAN_COLORS = {
  free:     { bg: '#E5E7EB', text: '#374151', badge: 'secondary' },
  silver:   { bg: '#F3F4F6', text: '#6B7280', badge: 'secondary' },
  gold:     { bg: '#FEF3C7', text: '#92400E', badge: 'warning' },
  platinum: { bg: '#EDE9FE', text: '#5B21B6', badge: 'primary' }
};

export const PlanProvider = ({ children }) => {
  const { user } = useAuth();
  const [planData, setPlanData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planError, setPlanError] = useState(null);

  const loadPlan = useCallback(async () => {
    if (!user) { setPlanData(null); setPlanError(null); setLoading(false); return; }
    setPlanError(null);
    try {
      const r = await billingService.getMyPlan();
      setPlanData(r.data || null);
    } catch (e) {
      setPlanError('Unable to load your subscription plan. Some features may appear restricted.');
      // Fall back to free plan so the app doesn't break, but keep the error visible
      setPlanData({ currentPlan: 'free', features: {}, limits: {}, _fallback: true });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { loadPlan(); }, [loadPlan]);

  // The axios response interceptor recursively camelCases all keys, so the
  // server's `features.destination_calculator` lands as `features.destinationCalculator`
  // on the client. Every <PlanGate> in the codebase passes snake_case feature
  // names (matching plans.php) — without this dual-lookup, every gated page
  // would show the upgrade wall to paid users. Same story for limits.
  const toCamel = (s) => s.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
  const hasFeature = (feature) => {
    const f = planData?.features;
    if (!f) return false;
    return f[feature] === true || f[toCamel(feature)] === true;
  };
  const getLimit = (key) => {
    const l = planData?.limits;
    if (!l) return 0;
    return l[key] ?? l[toCamel(key)] ?? 0;
  };
  const isPaid = planData?.currentPlan && planData.currentPlan !== 'free';
  const isTrialing = planData?.isTrialing;
  const plan = planData?.currentPlan || 'free';
  const isFallback = planData?._fallback === true;

  return (
    <PlanContext.Provider value={{ plan, planData, loading, planError, isFallback, hasFeature, getLimit, isPaid, isTrialing, reload: loadPlan }}>
      {children}
    </PlanContext.Provider>
  );
};

export const usePlan = () => {
  const ctx = useContext(PlanContext);
  if (!ctx) return { plan: 'free', hasFeature: () => false, getLimit: () => 0, isPaid: false, isTrialing: false, loading: false, planError: null, isFallback: true };
  return ctx;
};

export default PlanContext;
